/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/bookings
 * Password-gated. noindex. Not linked from any public page.
 * Shows all bookings with RepairShopr integration status and retry capability.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, RefreshCw, CheckCircle2, AlertCircle, Clock,
  Trash2, RotateCcw, ChevronDown, ChevronUp,
} from '../../lib/icons';
import SEOHead from '../../components/SEOHead';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BookingPayload {
  customer:          { first_name: string; last_name: string; email: string; phone: string };
  location:          { location_id: string; location_name: string };
  appointment:       { preferred_date: string; preferred_time: string; type?: string };
  selected_services: { service_name: string; price: number | null; quantity?: number }[];
  estimate:          { estimated_total: number };
  repairshopr?:      { subject?: string };
}

interface Booking {
  id:               string;
  created_at:       string;
  updated_at:       string;
  status:           'rs_pending' | 'rs_success' | 'rs_failed';
  payload:          BookingPayload;
  rs_ticket_id?:    number | null;
  rs_ticket_number?: number | null;
  rs_customer_id?:  number | null;
  rs_error?:        string | null;
  rs_field_errors?: Record<string, string[]> | null;
  rs_trace?:        string[] | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeader(): Record<string, string> {
  const t = localStorage.getItem('adminToken') ?? sessionStorage.getItem('adminToken') ?? '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function statusBadge(status: Booking['status']) {
  if (status === 'rs_success') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-950/60 border border-green-800 text-green-400 text-xs font-medium">
      <CheckCircle2 size={11} /> Created
    </span>
  );
  if (status === 'rs_failed') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-950/60 border border-red-800 text-red-400 text-xs font-medium">
      <AlertCircle size={11} /> Failed
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-950/60 border border-yellow-800 text-yellow-400 text-xs font-medium">
      <Clock size={11} /> Pending
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function BookingsContent() {
  const [bookings, setBookings]         = useState<Booking[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [filter, setFilter]             = useState<'all' | 'rs_failed' | 'rs_success'>('rs_failed');
  const [expanded, setExpanded]         = useState<Set<string>>(new Set());
  const [retrying, setRetrying]         = useState<Set<string>>(new Set());
  const [retryResults, setRetryResults] = useState<Record<string, { ok: boolean; msg: string }>>({});
  const [deleting, setDeleting]         = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = filter !== 'all' ? `?status=${filter}` : '';
      const r = await fetch(`/api/cms/bookings${qs}`, { headers: authHeader() });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json() as { bookings: Booking[] };
      setBookings(data.bookings ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  function toggleExpand(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function retry(booking: Booking) {
    setRetrying(prev => new Set(prev).add(booking.id));
    setRetryResults(prev => ({ ...prev, [booking.id]: { ok: false, msg: 'Retrying…' } }));
    try {
      const r = await fetch('/api/create-ticket', {
        method:  'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body:    JSON.stringify({ payload: booking.payload }),
      });
      const text = await r.text();
      type RetryData = { ok: boolean; ticketId?: number; ticketNumber?: number; error?: string; bookingId?: string };
      let data: RetryData;
      try { data = JSON.parse(text); } catch { data = { ok: false, error: `Non-JSON: ${text.slice(0, 200)}` }; }
      if (data.ok) {
        setRetryResults(prev => ({ ...prev, [booking.id]: { ok: true, msg: `Ticket #${data.ticketNumber} created` } }));
        load();
      } else {
        setRetryResults(prev => ({ ...prev, [booking.id]: { ok: false, msg: data.error ?? 'Unknown error' } }));
      }
    } catch (e) {
      setRetryResults(prev => ({ ...prev, [booking.id]: { ok: false, msg: (e as Error).message } }));
    } finally {
      setRetrying(prev => { const n = new Set(prev); n.delete(booking.id); return n; });
    }
  }

  async function deleteBooking(id: string) {
    if (!confirm('Delete this booking record? This cannot be undone.')) return;
    setDeleting(id);
    try {
      const r = await fetch(`/api/cms/bookings/${id}`, { method: 'DELETE', headers: authHeader() });
      if (!r.ok) throw new Error(await r.text());
      setBookings(prev => prev.filter(b => b.id !== id));
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setDeleting(null);
    }
  }

  const counts = {
    all:        bookings.length,
    rs_failed:  bookings.filter(b => b.status === 'rs_failed').length,
    rs_success: bookings.filter(b => b.status === 'rs_success').length,
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0] p-6">
      <SEOHead title="Bookings — GameFix Admin" noIndex />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/edit-site" className="text-[#888] hover:text-[#E0E0E0]">
            <Home size={18} />
          </Link>
          <span className="text-[#444]">/</span>
          <h1 className="text-lg font-bold text-[#E0E0E0]">Bookings</h1>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-[#aaa] hover:text-white hover:border-[#555] transition-colors disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5">
        {(['rs_failed', 'rs_success', 'all'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              filter === f
                ? f === 'rs_failed'
                  ? 'bg-red-950/60 border-red-700 text-red-300'
                  : f === 'rs_success'
                    ? 'bg-green-950/60 border-green-700 text-green-300'
                    : 'bg-[#1a1a1a] border-[#555] text-white'
                : 'bg-transparent border-[#333] text-[#888] hover:text-[#ccc]'
            }`}
          >
            {f === 'rs_failed' ? 'Failed' : f === 'rs_success' ? 'Created' : 'All'}
            {filter === f && <span className="ml-1.5 text-xs opacity-70">{counts[f]}</span>}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-950/30 border border-red-800 rounded-xl px-4 py-3 mb-4 text-red-400 text-sm flex items-center gap-3">
          <AlertCircle size={15} /> {error}
          <button onClick={load} className="ml-auto text-xs underline">Retry</button>
        </div>
      )}

      {loading && (
        <div className="text-[#666] text-sm py-10 text-center">Loading…</div>
      )}

      {!loading && !error && bookings.length === 0 && (
        <div className="text-[#666] text-sm py-10 text-center">
          {filter === 'rs_failed' ? 'No failed bookings.' : 'No bookings found.'}
        </div>
      )}

      {/* Booking list */}
      <div className="flex flex-col gap-3">
        {bookings.map(b => {
          const p = b.payload;
          const customer = p?.customer;
          const appt     = p?.appointment;
          const loc      = p?.location;
          const services = p?.selected_services ?? [];
          const isOpen   = expanded.has(b.id);
          const result   = retryResults[b.id];

          return (
            <div key={b.id} className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
              {/* Summary row */}
              <div className="flex items-start gap-4 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {statusBadge(b.status)}
                    {b.rs_ticket_number && (
                      <span className="text-xs text-[#39FF14] font-mono">#{b.rs_ticket_number}</span>
                    )}
                    <span className="text-xs text-[#666]">{fmt(b.created_at)}</span>
                  </div>
                  <div className="mt-1 font-medium text-sm">
                    {customer?.first_name} {customer?.last_name}
                    <span className="text-[#888] font-normal ml-2 text-xs">{customer?.email}</span>
                    <span className="text-[#666] font-normal ml-1 text-xs">{customer?.phone}</span>
                  </div>
                  <div className="text-xs text-[#888] mt-0.5">
                    {services.map(s => s.service_name).join(', ')}
                    <span className="text-[#555] mx-1">·</span>
                    {loc?.location_name}
                    {appt?.preferred_date && (
                      <><span className="text-[#555] mx-1">·</span>{appt.preferred_date} @ {appt.preferred_time}</>
                    )}
                  </div>
                  {b.rs_error && (
                    <div className="mt-1.5 text-xs text-red-400 font-mono bg-red-950/20 rounded px-2 py-1 border border-red-900/50 max-w-xl truncate">
                      {b.rs_error}
                    </div>
                  )}
                  {result && (
                    <div className={`mt-1.5 text-xs font-medium ${result.ok ? 'text-green-400' : 'text-red-400'}`}>
                      {result.ok ? '✓' : '✗'} {result.msg}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {b.status === 'rs_failed' && (
                    <button
                      onClick={() => retry(b)}
                      disabled={retrying.has(b.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF1744]/10 border border-[#FF1744]/40 rounded-lg text-xs text-[#FF1744] hover:bg-[#FF1744]/20 hover:border-[#FF1744]/60 transition-colors disabled:opacity-50"
                    >
                      <RotateCcw size={11} className={retrying.has(b.id) ? 'animate-spin' : ''} />
                      {retrying.has(b.id) ? 'Retrying…' : 'Retry'}
                    </button>
                  )}
                  <button
                    onClick={() => toggleExpand(b.id)}
                    className="p-1.5 text-[#555] hover:text-[#aaa] transition-colors"
                    title="Show details"
                  >
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  <button
                    onClick={() => deleteBooking(b.id)}
                    disabled={deleting === b.id}
                    className="p-1.5 text-[#555] hover:text-red-500 transition-colors disabled:opacity-50"
                    title="Delete booking record"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {/* Expanded detail */}
              {isOpen && (
                <div className="border-t border-[#1e1e1e] px-4 py-3 space-y-3 text-xs">
                  {/* Services */}
                  <div>
                    <div className="text-[#555] uppercase tracking-wider mb-1">Services</div>
                    {services.map((s, i) => (
                      <div key={i} className="flex justify-between text-[#aaa]">
                        <span>{s.service_name}{(s.quantity ?? 1) > 1 ? ` ×${s.quantity}` : ''}</span>
                        <span>{s.price !== null ? `$${s.price}` : 'Quote'}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold text-[#E0E0E0] border-t border-[#222] mt-1 pt-1">
                      <span>Est. Total</span>
                      <span>${p?.estimate?.estimated_total}</span>
                    </div>
                  </div>

                  {/* RS field errors */}
                  {b.rs_field_errors && Object.keys(b.rs_field_errors).length > 0 && (
                    <div>
                      <div className="text-[#555] uppercase tracking-wider mb-1">Field Errors</div>
                      {Object.entries(b.rs_field_errors).map(([field, msgs]) => (
                        <div key={field} className="text-red-400">
                          <span className="text-red-300 font-medium">{field}:</span> {(msgs as string[]).join(', ')}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Trace */}
                  {b.rs_trace && b.rs_trace.length > 0 && (
                    <details>
                      <summary className="text-[#555] cursor-pointer hover:text-[#888] uppercase tracking-wider">
                        Server trace ({b.rs_trace.length} lines)
                      </summary>
                      <pre className="mt-2 bg-black rounded-lg p-3 text-[#39FF14] text-[10px] leading-relaxed overflow-x-auto whitespace-pre-wrap break-all">
                        {b.rs_trace.join('\n')}
                      </pre>
                    </details>
                  )}

                  {/* Booking ID */}
                  <div className="text-[#444] font-mono pt-1">ID: {b.id}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminBookingsPage() {
  if (!isAdminAuthed()) return <AdminLoginGate />;
  return <BookingsContent />;
}
